import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import { Card, CardBody, CardTitle, Container, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap"
import toastr from "toastr"
import Breadcrumbs from "components/Common/Breadcrumb"
import { getAdminInfo } from "./actions"
import { resetPassword } from "./api"
import { adminListPath } from "./listQuery"
import { adminEmail, adminName, adminRole, adminStatus, adminCreatedAt, adminStatusDescription, canManageAdmins } from "./adminDisplay"
import "../../../assets/scss/custom/pages/_adminProfile.scss"
import AdminOperationDialog from "./AdminOperationDialog"
import { adminPermissions } from "./AdminRoleGuide"
import AccountInvitationPanel from '../AccountInvitationPanel'
import { adminInvitationLabels, runAdminCreationCommand } from './adminCreationOperation'
import SweetAlert from "react-bootstrap-sweetalert"

export const AdminProfile = ({ getAdminInfo, adminInfo = {}, loading, error, match: { params }, userDetails = {}, history, location }) => {
  const [operation, setOperation] = useState(null)
  const [reset, setReset] = useState(false)
  const [resetting, setResetting] = useState(false)
  const back = adminListPath(location.state?.adminListSearch)
  useEffect(() => { getAdminInfo(params.id) }, [getAdminInfo, params.id])
  const own = adminInfo.id === userDetails.id
  const canManage = canManageAdmins(userDetails) && adminInfo.status !== "deleted"
  const requestPasswordReset = async () => {
    if (resetting) return
    setResetting(true)
    try {
      const response = await resetPassword(adminInfo.email)
      if (response?.success === false) throw new Error()
      setReset(false)
      toastr.info("Password reset request sent.")
    } catch (error) { toastr.error("The password reset request could not be sent.") }
    finally { setResetting(false) }
  }
  return <div className="page-content admin-profile"><Container fluid>
    <Breadcrumbs path={back} title="Admins" breadcrumbItem="Admin Profile" />
    {loading || (!error && adminInfo.id !== params.id) ? <div className="text-center"><Spinner className="mt-5 mb-5" /></div>
      : error ? <Card><CardBody role="alert"><p>{error}</p><button type="button" className="btn btn-primary" onClick={() => getAdminInfo(params.id)}>Retry admin</button></CardBody></Card>
        : adminInfo.id && <>
          <Link to={back} className="admin-profile-back"><i className="bx bx-arrow-back mr-1" aria-hidden="true" />Back to admins</Link>
          <Card><CardBody className="admin-profile-header">
            <div className="admin-profile-identity">
              <h4 className="mb-2">{adminName(adminInfo)}</h4>
              <div className="admin-profile-meta">
                <span className="text-muted">#{adminInfo.userId ?? "—"}</span>
                <span>{adminRole(adminInfo.role)}</span>
                {adminStatus(adminInfo.status)}
                {own && <span className="badge badge-soft-info">You</span>}
              </div>
            </div>
            {canManage && <div className="admin-profile-actions">
              <Link className="btn btn-primary" to={{ pathname: "/accounts/admins/" + adminInfo.id + "/edit",
                state: { adminListSearch: location.state?.adminListSearch } }}>Edit admin</Link>
              <UncontrolledDropdown>
                <DropdownToggle className="btn btn-light" tag="button" type="button">More actions <i className="bx bx-chevron-down ml-1" aria-hidden="true" /></DropdownToggle>
                <DropdownMenu right>
                  {!adminInfo.adminActivationRequired && <DropdownItem onClick={() => setReset(true)}>Send password reset email</DropdownItem>}
                  {!own && <>
                    <DropdownItem divider />
                    {adminInfo.status !== "blocked" && <DropdownItem onClick={() => setOperation("blocked")}>Block access</DropdownItem>}
                    {!adminInfo.adminActivationRequired && adminInfo.status !== "activated" && <DropdownItem onClick={() => setOperation("activated")}>Activate account</DropdownItem>}
                    <DropdownItem divider />
                    <DropdownItem className="text-danger" onClick={() => setOperation("deleted")}>Delete account</DropdownItem>
                  </>}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>}
          </CardBody></Card>
          {location.state?.adminCreationResult && <div className="alert alert-success" role="status">Administrative account saved.
            {location.state.adminCreationResult.indexStatus === 'failed' && <p className="mb-0">Search indexing is delayed. The account is saved and available here.</p>}
          </div>}
          {typeof adminInfo.adminActivationRequired === 'boolean' && <AccountInvitationPanel id={adminInfo.id} actorId={userDetails.id}
            resource="admin" accountName="Administrator" labels={adminInvitationLabels} runCommand={runAdminCreationCommand} readOnly={!canManage} />}
          <div className="admin-profile-sections">
            <Card><CardBody>
              <CardTitle tag="h5" className="mb-4">Contact &amp; account</CardTitle>
              <dl className="admin-profile-details">
                <div><dt>Email</dt><dd>{adminEmail(adminInfo)}</dd></div>
                <div><dt>Account created</dt><dd>{adminCreatedAt(adminInfo.createdAt, true)}</dd></div>
              </dl>
            </CardBody></Card>
            <Card><CardBody>
              <CardTitle tag="h5" className="mb-4">Account access</CardTitle>
              <dl className="admin-profile-details">
                <div><dt>Administrative permissions</dt><dd>{adminPermissions(adminInfo.role)}</dd></div>
                <div><dt>Access status</dt><dd>{adminInfo.adminActivationRequired && adminInfo.status === "pending" ? "This administrator must set a password using the invitation before accessing Control Panel." : adminStatusDescription(adminInfo.status)}</dd></div>
              </dl>
              {own && <p className="text-muted mb-0">This is your account. You cannot block, delete or demote yourself.</p>}
              {!canManageAdmins(userDetails) && <p className="text-muted mb-0">You have read-only access to this administrative account.</p>}
            </CardBody></Card>
          </div>
        </>}
    {operation && <AdminOperationDialog key={operation} account={adminInfo} action={operation} onClose={() => setOperation(null)}
      onSuccess={() => {
        toastr.success(operation === "deleted" ? "Admin account deleted." : "Admin account status updated.")
        if (operation === "deleted") history.push(back)
        else getAdminInfo(params.id)
        setOperation(null)
      }} />}
    {reset && <SweetAlert title="Send password reset email?" warning showCancel confirmButtonText={resetting ? "Sending…" : "Send request"}
      disabled={resetting} onConfirm={requestPasswordReset} onCancel={() => { if (!resetting) setReset(false) }}>
      A reset email will be requested for {adminName(adminInfo)} (#{adminInfo.userId}).
    </SweetAlert>}
  </Container></div>
}
const mapState = ({ admins, session }) => ({ adminInfo: admins.adminInfo, loading: admins.loading, error: admins.error, userDetails: session.userDetails })
export default connect(mapState, { getAdminInfo })(withRouter(AdminProfile))
